const RUN_STYLE_KEYS = ['fontFamily', 'fontSize', 'fill', 'bold', 'italic', 'underline', 'strike'];

export const BLOCK_STYLE_DEFAULTS = {
  fontFamily: 'Inter',
  fontSize: 28,
  fill: '#111111',
  bold: false,
  italic: false,
  underline: false,
  strike: false,
};

const pickStyle = (style = {}) =>
  RUN_STYLE_KEYS.reduce((acc, key) => {
    if (style[key] !== undefined) {
      acc[key] = style[key];
    }
    return acc;
  }, {});

const sameStyle = (a = {}, b = {}) => RUN_STYLE_KEYS.every((key) => a[key] === b[key]);

export const textToRuns = (text = '') => [{ text: String(text), style: {} }];

export const runsToText = (runs = []) => runs.map((run) => run.text).join('');

export const normalizeRuns = (runs = []) => {
  const result = [];
  runs.forEach((run) => {
    if (!run.text) {
      return;
    }
    const style = pickStyle(run.style);
    const previous = result[result.length - 1];
    if (previous && sameStyle(previous.style, style)) {
      previous.text += run.text;
    } else {
      result.push({ text: run.text, style });
    }
  });
  return result.length ? result : [{ text: '', style: {} }];
};

export const getBlockStyle = (element = {}) =>
  RUN_STYLE_KEYS.reduce((acc, key) => {
    acc[key] = element[key] ?? BLOCK_STYLE_DEFAULTS[key];
    return acc;
  }, {});

export const resolveRunStyle = (blockStyle, runStyle = {}) => ({ ...blockStyle, ...pickStyle(runStyle) });

const pruneOverrides = (runs, blockStyle) =>
  normalizeRuns(
    runs.map((run) => {
      const style = { ...run.style };
      RUN_STYLE_KEYS.forEach((key) => {
        if (style[key] !== undefined && style[key] === blockStyle[key]) {
          delete style[key];
        }
      });
      return { ...run, style };
    }),
  );

const splitAt = (runs, start, end) => {
  const result = [];
  let offset = 0;
  runs.forEach((run) => {
    const runStart = offset;
    const runEnd = offset + run.text.length;
    offset = runEnd;
    const cuts = [runStart, runEnd];
    if (start > runStart && start < runEnd) {
      cuts.push(start);
    }
    if (end > runStart && end < runEnd) {
      cuts.push(end);
    }
    cuts.sort((a, b) => a - b);
    for (let i = 0; i < cuts.length - 1; i += 1) {
      if (cuts[i + 1] > cuts[i]) {
        result.push({
          text: run.text.slice(cuts[i] - runStart, cuts[i + 1] - runStart),
          style: run.style,
          start: cuts[i],
          end: cuts[i + 1],
        });
      }
    }
  });
  return result;
};

export const applyStyleToRange = (element, start, end, patch) => {
  const blockStyle = getBlockStyle(element);
  const runs = splitAt(element.runs, start, end).map((run) => {
    const inRange = run.start >= start && run.end <= end;
    return { text: run.text, style: inRange ? { ...run.style, ...pickStyle(patch) } : run.style };
  });
  return pruneOverrides(runs, blockStyle);
};

export const applyStyleToBlock = (element, patch) => {
  const changes = pickStyle(patch);
  const runs = element.runs.map((run) => {
    const style = { ...run.style };
    Object.keys(changes).forEach((key) => delete style[key]);
    return { ...run, style };
  });
  return { ...changes, runs: normalizeRuns(runs) };
};

export const getRangeStyle = (element, start, end) => {
  const blockStyle = getBlockStyle(element);
  if (end <= start) {
    return blockStyle;
  }
  const covered = splitAt(element.runs, start, end).filter((run) => run.start >= start && run.end <= end);
  if (!covered.length) {
    return blockStyle;
  }
  return RUN_STYLE_KEYS.reduce((acc, key) => {
    const values = new Set(covered.map((run) => resolveRunStyle(blockStyle, run.style)[key]));
    acc[key] = values.size === 1 ? [...values][0] : null;
    return acc;
  }, {});
};
